import React, { useRef } from 'react';
import { Point } from '../canvas/WhiteboardTypes';

interface MinimapProps {
  elements: Array<{
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    color?: string;
    fillColor?: string;
  }>;
  zoom: number;
  pan: Point;
  workspaceWidth: number;
  workspaceHeight: number;
  onPanChange: (newPan: Point) => void;
}

export const Minimap: React.FC<MinimapProps> = ({
  elements,
  zoom,
  pan,
  workspaceWidth,
  workspaceHeight,
  onPanChange,
}) => {
  const mapWidth = 160;
  const mapHeight = 120;
  const svgRef = useRef<SVGSVGElement | null>(null);

  // Compute viewport bounds on the virtual canvas
  const viewportX = -pan.x / zoom;
  const viewportY = -pan.y / zoom;
  const viewportWidth = workspaceWidth / zoom;
  const viewportHeight = workspaceHeight / zoom;

  // Compute total bounds of elements + viewport
  const allX = [viewportX, viewportX + viewportWidth, ...elements.map(el => el.x), ...elements.map(el => el.x + el.width)];
  const allY = [viewportY, viewportY + viewportHeight, ...elements.map(el => el.y), ...elements.map(el => el.y + el.height)];

  const minX = Math.min(...allX) - 200;
  const maxX = Math.max(...allX) + 200;
  const minY = Math.min(...allY) - 200;
  const maxY = Math.max(...allY) + 200;

  const boundsWidth = maxX - minX;
  const boundsHeight = maxY - minY;

  // Fit bounds into map size maintaining aspect ratio
  const scaleX = mapWidth / boundsWidth;
  const scaleY = mapHeight / boundsHeight;
  const scale = Math.min(scaleX, scaleY);

  // Center alignment offset
  const offsetX = (mapWidth - boundsWidth * scale) / 2;
  const offsetY = (mapHeight - boundsHeight * scale) / 2;

  // Convert canvas coordinates to minimap SVG coordinates
  const toMapX = (cx: number) => (cx - minX) * scale + offsetX;
  const toMapY = (cy: number) => (cy - minY) * scale + offsetY;
  const toMapSize = (cSize: number) => cSize * scale;

  // Convert minimap pointer coordinates back to canvas coordinates
  const mapToCanvas = (mx: number, my: number): Point => {
    const cx = (mx - offsetX) / scale + minX;
    const cy = (my - offsetY) / scale + minY;
    return { x: cx, y: cy };
  };

  const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    svgRef.current.setPointerCapture(e.pointerId);
    handlePointerDrag(e);
  };

  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!svgRef.current || !svgRef.current.hasPointerCapture(e.pointerId)) return;
    handlePointerDrag(e);
  };

  const handlePointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    if (svgRef.current) {
      try {
        svgRef.current.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Ignore capture release errors
      }
    }
  };

  const handlePointerDrag = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    // Convert map click center to target canvas center
    const targetCenter = mapToCanvas(mx, my);

    // Compute new pan offsets so target center is centered in workspace
    const newPanX = workspaceWidth / 2 - targetCenter.x * zoom;
    const newPanY = workspaceHeight / 2 - targetCenter.y * zoom;

    onPanChange({ x: newPanX, y: newPanY });
  };

  return (
    <div className="bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-800 rounded-xl p-1 shadow-lg pointer-events-auto select-none overflow-hidden transition-all duration-150">
      <svg
        ref={svgRef}
        width={mapWidth}
        height={mapHeight}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className="cursor-crosshair block"
      >
        {/* Draw background grid pattern in minimap */}
        <rect width="100%" height="100%" fill="rgba(148, 163, 184, 0.05)" />

        {/* Draw elements */}
        {elements.map(el => {
          const mx = toMapX(el.x);
          const my = toMapY(el.y);
          const mw = toMapSize(el.width);
          const mh = toMapSize(el.height);
          
          if (mw <= 0.5 || mh <= 0.5) return null;

          return (
            <rect
              key={el.id}
              x={mx}
              y={my}
              width={mw}
              height={mh}
              fill={el.fillColor || 'rgba(99, 102, 241, 0.2)'}
              stroke={el.color || 'rgba(99, 102, 241, 0.4)'}
              strokeWidth={1}
              rx={1}
              opacity={0.85}
            />
          );
        })}

        {/* Draw current viewport bounding box */}
        <rect
          x={toMapX(viewportX)}
          y={toMapY(viewportY)}
          width={toMapSize(viewportWidth)}
          height={toMapSize(viewportHeight)}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={1.5}
          rx={2}
        />
        <rect
          x={toMapX(viewportX)}
          y={toMapY(viewportY)}
          width={toMapSize(viewportWidth)}
          height={toMapSize(viewportHeight)}
          fill="#3b82f6"
          fillOpacity={0.06}
          rx={2}
        />
      </svg>
    </div>
  );
};
